import { computed, ref, watch } from 'vue'
import type { PostHog } from 'posthog-js'
import { initPostHog } from '@/store/modules/App/tracking/initPostHog'

const variantCache = new Map<
  string,
  string | null | Promise<string | undefined>
>()

let postHogInstance: PostHog | undefined
let flagUpdateListenerInitialized = false
const previousFlagValues = new Map<string, string | undefined>()

// Track invalidated flags - simple Set that gets recreated for reactivity
let invalidatedFlags = new Set<string>()

/**
 * Initiates the PostHog instance and sets up flag update listener
 */
function getPostHogInstance(): PostHog {
  if (!postHogInstance) {
    postHogInstance = initPostHog()
    setupFlagUpdateListener()
  }
  return postHogInstance
}

/**
 * Sets up a global listener to invalidate cache when flags are disabled
 */
function setupFlagUpdateListener(): void {
  if (flagUpdateListenerInitialized) {
    return
  }

  postHogInstance?.onFeatureFlags(() => {
    variantCache.forEach((_cachedValue, flagName) => {
      const currentValue = postHogInstance?.getFeatureFlag(flagName)
      const previousValue = previousFlagValues.get(flagName)

      const currentVariant =
        typeof currentValue === 'string' ? currentValue : undefined

      // Only invalidate when flag goes from enabled to disabled
      // (not when variant value changes - A/B tests should stay consistent)
      if (previousValue !== undefined && currentVariant === undefined) {
        variantCache.delete(flagName)
        invalidatedFlags = new Set([...invalidatedFlags, flagName])
      }

      previousFlagValues.set(flagName, currentVariant)
    })
  })

  flagUpdateListenerInitialized = true
}

/**
 * Validates and processes a PostHog feature flag variant.
 */
function validateVariant(
  variant: unknown,
  validOptions?: Record<string, string>,
): string | undefined {
  if (typeof variant !== 'string') {
    return undefined
  }

  if (validOptions) {
    const validValues = Object.values(validOptions)
    return validValues.includes(variant) ? variant : undefined
  }

  return variant
}

/**
 * Stores a variant value in cache and tracks it for change detection
 */
function cacheVariant(
  flagName: string,
  value: string | null,
  rawValue?: string | boolean | undefined,
): void {
  variantCache.set(flagName, value)
  // Only store string variants as PostHog can return boolean values
  const variantToStore =
    typeof rawValue === 'string' ? rawValue : (value ?? undefined)
  previousFlagValues.set(flagName, variantToStore)
}

/**
 * Waits for PostHog flags to load and returns the variant
 */
function waitForVariant(
  flagName: string,
  validOptions: Record<string, string> | undefined,
  fallbackValue: string | undefined,
): Promise<string | undefined> {
  const postHog = getPostHogInstance()

  return new Promise((resolve) => {
    let resolved = false

    // The timeout will resolve after 3 seconds if no valid variant appears
    const timeoutId = setTimeout(() => {
      if (resolved) {
        return
      }
      resolved = true
      unsubscribe()
      cacheVariant(flagName, null)
      resolve(fallbackValue)
    }, 3000)

    // onFeatureFlags returns a cleanup function that removes the listener
    const unsubscribe = postHog.onFeatureFlags(() => {
      if (resolved) {
        return
      }

      const variant = postHog.getFeatureFlag(flagName)
      const validated = validateVariant(variant, validOptions)

      if (validated !== undefined) {
        resolved = true
        clearTimeout(timeoutId)
        unsubscribe()
        cacheVariant(flagName, validated, variant)
        resolve(validated)
      }
      // If no valid variant, keep waiting - flags might still be loading
    })
  })
}
/**
 * Gets a PostHog feature flag variant value.
 * This function caches the result per flag name to ensure consistency
 * across the session and between different parts of the application.
 *
 * @param flagName - The name of the PostHog feature flag
 * @param validOptions - Optional object with valid variant values to validate against
 * @param fallbackValue - Optional fallback value to return when validation fails or flag is unavailable
 * @returns Promise that resolves to the variant name, fallback value, or undefined
 *
 * @example
 *
 * // With validation and fallback
 * const variant = await getPostHogVariant('my-feature-flag', {
 *   option1: 'option1',
 *   option2: 'option2',
 * }, 'option1')
 *
 * // Without validation (accepts any string variant)
 * const variant = await getPostHogVariant('my-feature-flag')
 */
export async function getPostHogVariant(
  flagName: string,
  validOptions?: Record<string, string>,
  fallbackValue?: string,
): Promise<string | undefined> {
  // Check cache - could be a value or a promise in progress
  if (variantCache.has(flagName)) {
    const cached = variantCache.get(flagName)!

    // If it's a promise, return it (await will handle it)
    if (cached instanceof Promise) {
      return cached
    }

    // If it's a resolved value, return it
    return cached === null ? fallbackValue : cached
  }

  const postHog = getPostHogInstance()

  // Check immediately in case flags are already loaded
  const variant = postHog.getFeatureFlag(flagName)
  const validated = validateVariant(variant, validOptions)

  if (validated !== undefined) {
    cacheVariant(flagName, validated, variant)
    return validated
  }

  const promise = waitForVariant(flagName, validOptions, fallbackValue)

  // Store promise in cache
  variantCache.set(flagName, promise)

  // Once resolved, replace promise with value only if it's still the current promise
  promise
    .then((result) => {
      // Only write back if cache entry still exists and is still this promise
      if (variantCache.get(flagName) === promise) {
        const value = result ?? null
        variantCache.set(flagName, value)
      }
    })
    .catch(() => {
      // Only write back if cache entry still exists and is still this promise
      if (variantCache.get(flagName) === promise) {
        variantCache.set(flagName, null)
      }
    })

  return promise
}

/**
 * Composable for getting PostHog feature flag variants in Vue components.
 * Returns a computed ref that updates when the variant is determined.
 * Uses {@link getPostHogVariant} internally.
 *
 * @param flagName - The name of the PostHog feature flag
 * @param validOptions - Optional object with valid variant values to validate against
 * @param fallbackValue - Optional fallback value to return when validation fails or flag is unavailable
 * @returns Computed ref with the variant value, fallback value, or undefined
 */
export function usePostHogVariant(
  flagName: string,
  validOptions?: Record<string, string>,
  fallbackValue?: string,
) {
  const variant = ref<string | undefined>(undefined)
  const lastInvalidatedFlags = ref(new Set<string>())

  function fetchVariant() {
    getPostHogVariant(flagName, validOptions, fallbackValue).then((value) => {
      variant.value = value
    })
  }

  // Initial fetch
  fetchVariant()

  // Watch for cache invalidation of this specific flag
  watch(
    () => invalidatedFlags.has(flagName),
    (isInvalidated) => {
      if (isInvalidated && !lastInvalidatedFlags.value.has(flagName)) {
        lastInvalidatedFlags.value.add(flagName)
        fetchVariant()
      }
    },
    { immediate: false },
  )

  return computed(() => variant.value)
}
